import { bool, func, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { withRouter } from 'react-router-dom';
import _cloneDeep from 'lodash/cloneDeep';

import { addSchemaValue } from 'ravenjs/utils/schema';
import { toast } from 'ravenjs/lib/Toaster';
import { get } from 'ravenjs/utils/lodash';
import Form from 'ravenjs/lib/Form';

import { updateTranslationUtil } from 'utils/esignature';

import {
    DEFAULT_REMIND_NOTE_TO_SIGNER,
    DEFAULT_REMIND_SUBJECT,
    DEFAULT_REMIND_NOTE_TO_SIGNER_ES,
} from 'constants/esignature';
import { actions as esigActions } from 'modules/esigDocuments';
import { actions as adminActions } from 'modules/admin';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import { selectors as authSelectors } from 'modules/auth';
import { selectors as userSelectors } from 'modules/user';
import { MODALS } from 'constants/modals';
import schema from './schema.json';
import Modal from '../Modal';

class EsignRemindModal extends Component {
    static propTypes = {
        accessToken: string,
        closeModal: func.isRequired,
        createConfig: func.isRequired,
        history: object.isRequired,
        isLoading: func.isRequired,
        remindEsignatureRequest: func.isRequired,
        modal: shape({
            data: object,
            open: bool,
        }),
        openModal: func.isRequired,
        hideIcon: bool,
    };

    static defaultProps = {
        accessToken: null,
        modal: {
            open: false,
            data: null,
        },
        hideIcon: false,
    };

    constructor(props) {
        super(props);

        this.formRef = React.createRef();

        this.state = {
            formData: {
                mailSubject: DEFAULT_REMIND_SUBJECT,
                noteToSigner: DEFAULT_REMIND_NOTE_TO_SIGNER,
                noteToSignersEs: DEFAULT_REMIND_NOTE_TO_SIGNER_ES,
            },
            originalSchema: _cloneDeep(schema),
        };
    }

    getModalProps = () => {
        const { modal } = this.props;
        const ModalProps = get(modal, 'data.ModalProps', {});
        const actions = get(modal, 'data.actions', []);
        if (actions[1]) {
            actions[1].onClick = this.hanleSendRemindRequest;
        }
        const cancelBtnProps = get(modal, 'data.cancelBtnProps');
        const onCancel = get(modal, 'data.onCancel');
        const onSuccess = get(modal, 'data.onSuccess');
        const open = get(modal, 'open', false);
        const successBtnProps = get(modal, 'data.successBtnProps');
        return {
            actions,
            cancelBtnProps,
            content: this.renderContent(),
            id: MODALS.SEND_FOR_ESIG_MODAL,
            onCancel,
            onSuccess,
            open,
            successBtnProps,
            ModalProps: {
                ContentProps: {
                    style: {
                        maxWidth: 550,
                    },
                },
                ...ModalProps,
            },
            modalBodyProps: {
                padding: '15px 15px 0',
            },
        };
    };

    handleCloseModal = () => {
        const { closeModal } = this.props;
        closeModal(MODALS.ESIGN_REMIND_MODAL);
    };

    hanleSendRemindRequest = () => {
        const { closeModal, modal, remindEsignatureRequest } = this.props;
        const { formData } = this.state;
        const documentInfo = get(modal, 'data.documentInfo');
        const { documentLanguage, documentState } = documentInfo;
        const uuid = get(documentInfo, 'uuid');
        const payload = {
            uuid,
            documentLanguage,
            documentState,
            ...formData,
        };
        remindEsignatureRequest(payload)
            .then(() => {
                closeModal(MODALS.ESIGN_REMIND_MODAL);
                toast.success('Confirmation: Sent a reminder for e-Signature..');
            })
            .catch(() => {
                closeModal(MODALS.ESIGN_REMIND_MODAL);
                toast.error('Unable to send an e-signature reminder.');
            });
    };

    handleOnCancel = () => {
        const { closeModal } = this.props;
        closeModal(MODALS.ESIGN_REMIND_MODAL);
    };

    handleFormChange = ({ formData }) => {
        this.setState({
            formData,
        });
    };

    updateTranslation = async () => {
        const { formData } = this.state;
        const { modal } = this.props;
        const updateTranslationFunction = get(modal, 'data.updateTranslation');

        const updatedFormData = await updateTranslationUtil({
            formData,
            updateTranslation: updateTranslationFunction,
            singleObject: true,
        });

        this.setState({
            formData: updatedFormData,
        });
    };

    renderContent = () => {
        const { formData, originalSchema } = this.state;
        const { modal } = this.props;
        const documentLanguage = get(modal, 'data.documentInfo.documentLanguage');
        const documentName = get(modal, 'data.documentInfo.documentName');
        const schemaProperties = originalSchema.schema.properties;

        formData.mailSubject = `${DEFAULT_REMIND_SUBJECT}: ${documentName}`;

        if (documentLanguage === 'es') {
            addSchemaValue(schema, 'schema.properties', {
                ...schemaProperties,
                noteToSignersEs: {
                    title: 'Note for Signers ES',
                    type: 'string',
                },
            });
            addSchemaValue(
                schema,
                'uiSchema.noteToSignersEs.ui:options.optionalLink.callbackFunction',
                this.updateTranslation
            );
        } else {
            addSchemaValue(schema, 'schema.properties', schemaProperties);
        }

        return (
            <Form {...schema} hideActions formData={formData} onChange={this.handleFormChange} />
        );
    };

    render() {
        return <Modal {...this.getModalProps()} />;
    }
}

const mapStateToProps = createStructuredSelector({
    modal: uiSelectors.getModalById(MODALS.ESIGN_REMIND_MODAL),
    accessToken: authSelectors.getAccessToken,
    userCompany: userSelectors.getUserCompany,
    userRole: userSelectors.getUserRole,
});

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    openModal: uiActions.openModal,
    createConfig: adminActions.createConfig,
    isLoading: uiActions.isLoading,
    remindEsignatureRequest: esigActions.remindEsignatureRequest,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EsignRemindModal));
